import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import store from "../redux-store/store";
import config from "../../config";
import { validateStatus } from "./errorHandling";
import {
  FetchActionsResponse,
  FetchAuditsResponse,
  FetchAuditTypesResponse,
  FetchConstatGravitiesResponse,
  FetchConstatsResponse,
  FetchEventsResponse,
  FetchProjectsResponse,
  FetchRoleResponse,
  FetchSiteResponse,
  FetchTemesResponse,
  FetchThemesResponse,
  FetchUsersForFieldResponse,
  FetchUsersResponse,
  UserCreateResponse,
  UserUpdateResponse,
} from "../../types/apiResponsesTypes";
import { FormUserValues } from "../../types/user";
import {
  Action,
  FormActionPostValues,
  FormActionValidatePostValues,
  FormMarkAsPerformedPostValues,
} from "../../types/actions";
import {
  Event,
  FormEventCauseAnalysisPostValues,
  FormEventDispatchPilotePostValues,
  FormEventPostValues,
  FormEventValidatePostValues,
} from "../../types/events";
import { CauseAnalysis } from "../../types/causeAnalysis";
import { RoleName } from "../../types/role";
import { DashboardStatistics } from "../../types/dashboardStatistics";
import { Comment, CommentPostValues } from "../../types/comments";
import { Theme, ThemePostValues } from "../../types/thematiques";
import { AuditType, AuditTypePostValues } from "../../types/auditTypes";
import { Audit, AuditPostValues } from "../../types/audits";
import {
  ConstatGravity,
  ConstatGravityPostValues,
} from "../../types/constatGravities";
import {
  Constat,
  ConstatPostValues,
  ConstatValidatePostValues,
} from "../../types/constats";
import { Project, ProjectPostValues } from "../../types/projects";

/**
 * Links
 * redux-toolkit uses RTK Query
 * https://redux-toolkit.js.org/rtk-query/overview
 * https://redux-toolkit.js.org/rtk-query/api/created-api/hooks
 */

/**
 * createApi doc: https://redux-toolkit.js.org/rtk-query/api/createApi
 */
export const apiSlice = createApi({
  reducerPath: "api",
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: config.apiUrl,
    prepareHeaders(headers, { getState }) {
      const state = (getState as typeof store.getState)();
      const {
        auth: { user },
      } = state;

      headers.set("Authorization", `Bearer ${user?.access_token || ""}`);
      // headers.set("appVersion", config.appVersion);
      // headers.set("environment", config.env);

      return headers;
    },
  }),
  endpoints(builders) {
    return {
      // [requestName]: builders.query<ResponseType, ArgumentType>(
      /**
       * Events
       */
      fetchEvents: builders.query<FetchEventsResponse, void>({
        query: () => ({
          url: `evt/events`,
          validateStatus,
        }),
        // transformResponse(response: FetchGlobalDataResponse, meta, arg) {
        //   return response;
        // },
        // transformErrorResponse: (response, meta, arg) => {
        //   return response;
        // },
      }),
      fetchEvent: builders.query<Event, number>({
        query: (id: number) => ({
          url: `evt/events/${id}`,
          validateStatus,
          cache: "no-cache",
        }),
      }),
      fetchPaginatedEvents: builders.query<FetchEventsResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/events?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchPaginatedDashboardEvents: builders.query<
        FetchEventsResponse,
        string | void
      >({
        query: (queryParams?: string) => ({
          url: `evt/dashboard/events?${queryParams}`,
          validateStatus,
        }),
      }),
      eventCreate: builders.mutation<Event, FormEventPostValues>({
        query: (values) => {
          return {
            url: "evt/events",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      eventUpdate: builders.mutation<
        Event,
        {
          eventId: number;
          values: FormEventPostValues;
        }
      >({
        query: ({ eventId, values }) => {
          return {
            url: `evt/events/${eventId}`,
            method: "PATCH",
            validateStatus,
            body: values,
          };
        },
      }),
      eventUploadAttachments: builders.mutation<
        Event["attachedFiles"],
        {
          eventId: number;
          files: File[];
        }
      >({
        query: ({ eventId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/events/${eventId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      eventDispatchPilote: builders.mutation<
        Event,
        {
          eventId: number;
          values: FormEventDispatchPilotePostValues;
        }
      >({
        query: ({ eventId, values }) => {
          return {
            url: `evt/events/${eventId}/dispatchPilote`,
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      eventAskValidation: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}/ask-validation`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventValidateOrRefuse: builders.mutation<
        Event,
        FormEventValidatePostValues & {
          validateOrRefuse: "validate" | "refused";
        }
      >({
        query: ({ eventId, validateOrRefuse, comment }) => {
          const slug = validateOrRefuse === "validate" ? "close" : "refuse";
          return {
            url: `evt/events/${eventId}/${slug}?comment=${comment}`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventReopen: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}/reopen`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      eventArchive: builders.mutation<Event, number>({
        query: (eventId) => {
          return {
            url: `evt/events/${eventId}`,
            method: "DELETE",
            validateStatus,
            body: {},
          };
        },
      }),
      /**
       * Event - Cause analysis
       */
      createCauseAnalysis: builders.mutation<
        CauseAnalysis,
        FormEventCauseAnalysisPostValues
      >({
        query: (values) => {
          return {
            url: "evt/cause-analysis",
            method: "POST",
            validateStatus,
            body: {
              eventType: values.eventType,
              description: values.description,
              pilote: values.pilote,
              event: values.event,
            },
          };
        },
      }),
      causeUploadAttachments: builders.mutation<
        void,
        {
          causeId: number;
          files: File[];
        }
      >({
        query: ({ causeId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/cause-analysis/${causeId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),

      /**
       * Actions
       */
      fetchPaginatedActions: builders.query<
        FetchActionsResponse,
        string | undefined
      >({
        query: (queryParams?: string) => ({
          url: `evt/actions?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchPaginatedDashboardActions: builders.query<
        FetchActionsResponse,
        string | undefined
      >({
        query: (queryParams?: string) => ({
          url: `evt/dashboard/actions?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchAction: builders.query<Action, string | undefined>({
        query: (id?: string) => ({
          url: `evt/actions/${id}`,
          validateStatus,
        }),
      }),
      actionCreate: builders.mutation<Action, FormActionPostValues>({
        query: (values) => {
          return {
            url: "evt/actions",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      actionUpdate: builders.mutation<
        Action,
        {
          actionId: number;
          values: FormActionPostValues;
        }
      >({
        query: ({ actionId, values }) => {
          return {
            url: `evt/actions/${actionId}`,
            method: "PATCH",
            validateStatus,
            body: values,
          };
        },
      }),
      actionDelete: builders.mutation<void, number>({
        query: (actionId) => {
          return {
            url: `evt/actions/${actionId}`,
            method: "DELETE",
            validateStatus,
          };
        },
      }),

      actionUploadAttachments: builders.mutation<
        Action["attachedFiles"],
        {
          actionId: number;
          files: File[];
        }
      >({
        query: ({ actionId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/actions/${actionId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      actionPerformed: builders.mutation<Action, FormMarkAsPerformedPostValues>(
        {
          query: ({ actionId, dateDone, comment }) => {
            return {
              url: `evt/actions/${actionId}/performed?comment=${comment}&dateDone${dateDone}`,
              method: "PATCH",
              validateStatus,
              body: {},
            };
          },
        },
      ),
      actionValidateOrRefuse: builders.mutation<
        Action,
        FormActionValidatePostValues & {
          validateOrRefuse: "validate" | "refused";
        }
      >({
        query: ({ actionId, validateOrRefuse, comment }) => {
          return {
            url: `evt/actions/${actionId}/${validateOrRefuse}?comment=${comment}`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      /**
       * Users
       */
      fetchUsers: builders.query<FetchUsersResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/users?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchUsersForField: builders.query<
        FetchUsersForFieldResponse,
        "" | RoleName
      >({
        query: (role) => ({
          url: role ? `evt/users/list?role=${role}` : `evt/users/list`,
          validateStatus,
        }),
      }),
      userCreate: builders.mutation<UserCreateResponse, FormUserValues>({
        query: (values) => {
          return {
            url: "evt/users",
            method: "POST",
            validateStatus,
            body: values,
          };
        },
      }),
      userUpdate: builders.mutation<
        UserUpdateResponse,
        {
          userId: string;
          values: FormUserValues;
        }
      >({
        query: ({ userId, values }) => {
          return {
            url: `evt/users/${userId}`,
            method: "PUT",
            validateStatus,
            body: values,
          };
        },
      }),
      userDelete: builders.mutation<void, number>({
        query: (userId) => {
          return {
            url: `evt/users/${userId}`,
            method: "DELETE",
            validateStatus,
          };
        },
      }),
      /**
       * Comments
       */
      fetchEventComments: builders.query<Comment[], number>({
        query: (eventId) => ({
          url: `evt/events/${eventId}/comments`,
          validateStatus,
        }),
      }),
      createEventComment: builders.mutation<Comment, CommentPostValues>({
        query: (values) => ({
          url: `evt/comments`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      fetchActionComments: builders.query<Comment[], number>({
        query: (actionId) => ({
          url: `evt/actions/${actionId}/comments`,
          validateStatus,
        }),
      }),
      createActionComment: builders.mutation<Comment, CommentPostValues>({
        query: (values) => ({
          url: `evt/comments`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      /**
       * Other (status, role, site)
       */
      fetchDashboardStatistics: builders.query<
        DashboardStatistics,
        { startDate?: string; endDate?: string } | void
      >({
        query: (params) => ({
          url: `evt/dashboard/stats`,
          params: params ?? undefined,
          validateStatus,
        }),
      }),
      fetchRoles: builders.query<FetchRoleResponse, void>({
        query: () => ({
          url: `evt/roles`,
          validateStatus,
          // cache: "no-cache",
        }),
      }),
      fetchSites: builders.query<FetchSiteResponse, void>({
        query: () => ({
          url: `evt/sites`,
          validateStatus,
        }),
      }),
      fetchThemes: builders.query<FetchTemesResponse, void>({
        query: () => ({
          url: `evt/themes`,
          validateStatus,
        }),
      }),
      fetchPaginatedThemes: builders.query<FetchThemesResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/themes?${queryParams}`,
          validateStatus,
        }),
      }),
      createTheme: builders.mutation<Theme, ThemePostValues>({
        query: (values) => ({
          url: `evt/themes`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      updateTheme: builders.mutation<Theme, { themeId: number; values: ThemePostValues }>({
        query: ({ themeId, values }) => ({
          url: `evt/themes/${themeId}`,
          method: "PUT",
          validateStatus,
          body: values,
        }),
      }),
      deleteTheme: builders.mutation<void, number>({
        query: (themeId) => ({
          url: `evt/themes/${themeId}`,
          method: "DELETE",
          validateStatus,
        }),
      }),
      fetchPaginatedAuditTypes: builders.query<FetchAuditTypesResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/audit-types?${queryParams}`,
          validateStatus,
        }),
      }),
      createAuditType: builders.mutation<AuditType, AuditTypePostValues>({
        query: (values) => ({
          url: `evt/audit-types`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      updateAuditType: builders.mutation<
        AuditType,
        { auditTypeId: number; values: AuditTypePostValues }
      >({
        query: ({ auditTypeId, values }) => ({
          url: `evt/audit-types/${auditTypeId}`,
          method: "PUT",
          validateStatus,
          body: values,
        }),
      }),
      deleteAuditType: builders.mutation<void, number>({
        query: (auditTypeId) => ({
          url: `evt/audit-types/${auditTypeId}`,
          method: "DELETE",
          validateStatus,
        }),
      }),
      fetchAuditTypes: builders.query<FetchAuditTypesResponse, void>({
        query: () => ({
          url: `evt/audit-types`,
          validateStatus,
        }),
      }),
      fetchPaginatedConstatGravities: builders.query<
        FetchConstatGravitiesResponse,
        string | void
      >({
        query: (queryParams?: string) => ({
          url: `evt/constat-gravities?${queryParams}`,
          validateStatus,
        }),
      }),
      createConstatGravity: builders.mutation<
        ConstatGravity,
        ConstatGravityPostValues
      >({
        query: (values) => ({
          url: `evt/constat-gravities`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      updateConstatGravity: builders.mutation<
        ConstatGravity,
        { constatGravityId: number; values: ConstatGravityPostValues }
      >({
        query: ({ constatGravityId, values }) => ({
          url: `evt/constat-gravities/${constatGravityId}`,
          method: "PUT",
          validateStatus,
          body: values,
        }),
      }),
      deleteConstatGravity: builders.mutation<void, number>({
        query: (constatGravityId) => ({
          url: `evt/constat-gravities/${constatGravityId}`,
          method: "DELETE",
          validateStatus,
        }),
      }),
      fetchConstatGravities: builders.query<FetchConstatGravitiesResponse, void>({
        query: () => ({
          url: `evt/constat-gravities`,
          validateStatus,
        }),
      }),
      fetchAudit: builders.query<Audit, number>({
        query: (id: number) => ({
          url: `evt/audits/${id}`,
          validateStatus,
          cache: "no-cache",
        }),
      }),
      fetchPaginatedAudits: builders.query<FetchAuditsResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/audits?${queryParams}`,
          validateStatus,
        }),
      }),
      createAudit: builders.mutation<Audit, AuditPostValues>({
        query: (values) => ({
          url: `evt/audits`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      updateAudit: builders.mutation<Audit, { auditId: number; values: AuditPostValues }>({
        query: ({ auditId, values }) => ({
          url: `evt/audits/${auditId}`,
          method: "PUT",
          validateStatus,
          body: values,
        }),
      }),
      deleteAudit: builders.mutation<void, number>({
        query: (auditId) => ({
          url: `evt/audits/${auditId}`,
          method: "DELETE",
          validateStatus,
        }),
      }),
      closeAudit: builders.mutation<Audit, number>({
        query: (auditId) => ({
          url: `evt/audits/${auditId}/close`,
          method: "PATCH",
          validateStatus,
          body: {},
        }),
      }),
      auditUploadAttachments: builders.mutation<
        Audit["attachedFiles"],
        { auditId: number; files: File[] }
      >({
        query: ({ auditId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/audits/${auditId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      fetchAuditConstats: builders.query<
        FetchConstatsResponse,
        { auditId: number; queryParams?: string }
      >({
        query: ({ auditId, queryParams }) => ({
          url: `evt/audits/${auditId}/constats?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchAudits: builders.query<FetchAuditsResponse, void>({
        query: () => ({
          url: `evt/audits`,
          validateStatus,
        }),
      }),
      fetchPaginatedConstats: builders.query<FetchConstatsResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/constats?${queryParams}`,
          validateStatus,
        }),
      }),
      fetchPaginatedDashboardConstats: builders.query<
        FetchConstatsResponse,
        string | void
      >({
        query: (queryParams?: string) => ({
          url: `evt/dashboard/constats?${queryParams}`,
          validateStatus,
        }),
      }),
      createConstat: builders.mutation<Constat, ConstatPostValues>({
        query: (values) => ({
          url: `evt/constats`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      deleteConstat: builders.mutation<void, number>({
        query: (constatId) => ({
          url: `evt/constats/${constatId}`,
          method: "DELETE",
          validateStatus,
        }),
      }),
      fetchConstat: builders.query<Constat, number>({
        query: (id: number) => ({
          url: `evt/constats/${id}`,
          validateStatus,
          cache: "no-cache",
        }),
      }),
      constatUpdate: builders.mutation<
        Constat,
        { constatId: number; values: ConstatPostValues }
      >({
        query: ({ constatId, values }) => ({
          url: `evt/constats/${constatId}`,
          method: "PATCH",
          validateStatus,
          body: values,
        }),
      }),
      constatUploadAttachments: builders.mutation<
        Constat["attachedFiles"],
        { constatId: number; files: File[] }
      >({
        query: ({ constatId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/constats/${constatId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      constatAskValidation: builders.mutation<Constat, number>({
        query: (constatId) => ({
          url: `evt/constats/${constatId}/ask-validation`,
          method: "PATCH",
          validateStatus,
          body: {},
        }),
      }),
      constatValidateOrRefuse: builders.mutation<
        Constat,
        ConstatValidatePostValues & { validateOrRefuse: "validate" | "refused" }
      >({
        query: ({ constatId, validateOrRefuse, comment }) => {
          const slug = validateOrRefuse === "validate" ? "close" : "refuse";
          return {
            url: `evt/constats/${constatId}/${slug}?comment=${comment}`,
            method: "PATCH",
            validateStatus,
            body: {},
          };
        },
      }),
      constatReopen: builders.mutation<Constat, number>({
        query: (constatId) => ({
          url: `evt/constats/${constatId}/reopen`,
          method: "PATCH",
          validateStatus,
          body: {},
        }),
      }),
      fetchConstatComments: builders.query<Comment[], number>({
        query: (constatId) => ({
          url: `evt/constats/${constatId}/comments`,
          validateStatus,
        }),
      }),
      /**
       * Projects CRUD
       */
      fetchPaginatedProjects: builders.query<FetchProjectsResponse, string | void>({
        query: (queryParams?: string) => ({
          url: `evt/projects?${queryParams}`,
          validateStatus,
        }),
      }),
      createProject: builders.mutation<Project, ProjectPostValues>({
        query: (values) => ({
          url: `evt/projects`,
          method: "POST",
          validateStatus,
          body: values,
        }),
      }),
      fetchProject: builders.query<Project, number>({
        query: (id: number) => ({
          url: `evt/projects/${id}`,
          validateStatus,
          cache: "no-cache",
        }),
      }),
      projectUpdate: builders.mutation<
        Project,
        { projectId: number; values: ProjectPostValues }
      >({
        query: ({ projectId, values }) => ({
          url: `evt/projects/${projectId}`,
          method: "PATCH",
          validateStatus,
          body: values,
        }),
      }),
      projectUploadAttachments: builders.mutation<
        Project["attachedFiles"],
        { projectId: number; files: File[] }
      >({
        query: ({ projectId, files }) => {
          const formData = new FormData();
          for (const file of files) {
            formData.append("files", file);
          }
          return {
            url: `evt/projects/${projectId}/attachment`,
            method: "POST",
            validateStatus,
            body: formData,
          };
        },
      }),
      projectClose: builders.mutation<Project, number>({
        query: (projectId) => ({
          url: `evt/projects/${projectId}/close`,
          method: "PATCH",
          validateStatus,
          body: {},
        }),
      }),
      projectArchive: builders.mutation<Project, number>({
        query: (projectId) => ({
          url: `evt/projects/${projectId}`,
          method: "DELETE",
          validateStatus,
          body: {},
        }),
      }),
      fetchProjectComments: builders.query<Comment[], number>({
        query: (projectId) => ({
          url: `evt/projects/${projectId}/comments`,
          validateStatus,
        }),
      }),
      //   tiggerError404: builders.query<FetchGlobalDataResponse, void>({
      //     query: () => ({
      //       url: `BAD_URL_FOR_404`,
      //       validateStatus,
      //     }),
      //   }),
      //   tiggerError401: builders.query<FetchGlobalDataResponse, void>({
      //     query: () => ({
      //       url: `provoke-unauthorized`,
      //       validateStatus,
      //     }),
      //   }),
    };
  },
});

export const {
  // Events
  useFetchEventQuery,
  useEventCreateMutation,
  useEventUpdateMutation,
  useLazyFetchEventsQuery,
  useLazyFetchPaginatedEventsQuery,
  useLazyFetchPaginatedDashboardEventsQuery,
  useEventUploadAttachmentsMutation,
  useCauseUploadAttachmentsMutation,
  useEventDispatchPiloteMutation,
  useCreateCauseAnalysisMutation,
  useEventAskValidationMutation,
  useEventValidateOrRefuseMutation,
  useEventArchiveMutation,
  useEventReopenMutation,
  // Actions
  useLazyFetchPaginatedActionsQuery,
  useLazyFetchPaginatedDashboardActionsQuery,
  useFetchActionQuery,
  useActionCreateMutation,
  useActionUpdateMutation,
  useActionDeleteMutation,
  useActionUploadAttachmentsMutation,
  useActionPerformedMutation,
  useActionValidateOrRefuseMutation,
  // Users
  useUserCreateMutation,
  useFetchUsersQuery,
  useFetchUsersForFieldQuery,
  useLazyFetchUsersQuery,
  useUserUpdateMutation,
  useUserDeleteMutation,
  // Comments
  useFetchEventCommentsQuery,
  useCreateEventCommentMutation,
  useFetchActionCommentsQuery,
  useCreateActionCommentMutation,
  // Other
  useFetchDashboardStatisticsQuery,
  useFetchRolesQuery,
  useFetchSitesQuery,
  useFetchThemesQuery,
  // Themes CRUD
  useLazyFetchPaginatedThemesQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
  useDeleteThemeMutation,
  // Audit Types CRUD
  useLazyFetchPaginatedAuditTypesQuery,
  useCreateAuditTypeMutation,
  useUpdateAuditTypeMutation,
  useDeleteAuditTypeMutation,
  useFetchAuditTypesQuery,
  // Constat Gravities CRUD
  useLazyFetchPaginatedConstatGravitiesQuery,
  useCreateConstatGravityMutation,
  useUpdateConstatGravityMutation,
  useDeleteConstatGravityMutation,
  useFetchConstatGravitiesQuery,
  // Audits CRUD
  useFetchAuditQuery,
  useLazyFetchPaginatedAuditsQuery,
  useCreateAuditMutation,
  useUpdateAuditMutation,
  useDeleteAuditMutation,
  useCloseAuditMutation,
  useAuditUploadAttachmentsMutation,
  useLazyFetchAuditConstatsQuery,
  useFetchAuditsQuery,
  // Constats CRUD
  useLazyFetchPaginatedConstatsQuery,
  useLazyFetchPaginatedDashboardConstatsQuery,
  useCreateConstatMutation,
  useDeleteConstatMutation,
  useFetchConstatQuery,
  useConstatUpdateMutation,
  useConstatUploadAttachmentsMutation,
  useConstatAskValidationMutation,
  useConstatValidateOrRefuseMutation,
  useConstatReopenMutation,
  useFetchConstatCommentsQuery,
  // Projects CRUD
  useLazyFetchPaginatedProjectsQuery,
  useCreateProjectMutation,
  useFetchProjectQuery,
  useProjectUpdateMutation,
  useProjectUploadAttachmentsMutation,
  useProjectCloseMutation,
  useProjectArchiveMutation,
  useFetchProjectCommentsQuery,
} = apiSlice;
